import { Children, useId, useRef } from 'react'
import { X } from 'lucide-react'
import { resolveComponentVariant } from '../componentContract'
import { IconButton } from '../primitives/IconButton'
import { OverlayPortal, useOverlayLifecycle } from './OverlayFoundation'
import styles from './Overlays.module.css'

const sizes = ['small', 'default', 'large', 'full', 'media']
const sizeAliases = { sm: 'small', md: 'default', lg: 'large', xl: 'large' }

export function Dialog({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  size = 'default',
  dismissable = true,
  hideCloseButton = false,
  initialFocusRef,
  returnFocusRef,
  returnFocusFallbackRef,
  className = '',
  contentClassName = '',
  labelledBy,
}) {
  const surfaceRef = useRef(null)
  const titleId = useId()
  const descriptionId = useId()
  const resolvedSize = resolveComponentVariant('Dialog size', sizeAliases[size] || size, sizes, 'default')
  useOverlayLifecycle({ open, onClose, dismissable, modal: true, trapFocus: true, surfaceRef, initialFocusRef, returnFocusRef, returnFocusFallbackRef })
  if (!open) return null
  // A dialog that is only a question (a confirm) has no body: the footer
  // follows the header, with no empty padded band between them.
  const hasBody = Children.toArray(children).length > 0

  return (
    <OverlayPortal>
      <div
        className={styles.backdrop}
        data-testid="backdrop"
        onClick={(event) => {
          if (dismissable && event.target === event.currentTarget) onClose?.()
        }}
      >
        <div
          ref={surfaceRef}
          className={`${styles.surface} ${styles[`dialog${resolvedSize[0].toUpperCase()}${resolvedSize.slice(1)}`]} ${className}`.trim()}
          data-testid="dialog-content"
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy || (title ? titleId : undefined)}
          aria-describedby={description ? descriptionId : undefined}
          tabIndex={-1}
        >
          {(title || description || !hideCloseButton) ? (
            <header className={styles.header}>
              <div className={styles.heading}>
                {title ? <h2 id={titleId} className={styles.title}>{title}</h2> : null}
                {description ? <p id={descriptionId} className={styles.description}>{description}</p> : null}
              </div>
              {!hideCloseButton ? <IconButton variant="ghost" label="Close dialog" onClick={onClose}><X size={18} aria-hidden="true" /></IconButton> : null}
            </header>
          ) : null}
          {hasBody ? <div className={`${styles.content} ${contentClassName}`.trim()}>{children}</div> : null}
          {footer ? <footer className={styles.footer}>{footer}</footer> : null}
        </div>
      </div>
    </OverlayPortal>
  )
}
